import {
  AdminFetchDocumentReviewSteps,
  AdminFetchDocumentReviewStep,
  AdminFetchReviewRejectionReasons,
  AdminApproveReviewStep,
  AdminRecollectReviewStep,
} from 'src/constants/actionTypes'
import type {
  DocumentReviewQueueRow,
  DocumentReviewStep,
  ReviewRejectionReason,
} from 'src/interfaces/documentReview'
import { extractApiErrorMessages } from 'src/utils/errorHandlers'
import { Action } from 'src/utils/reduxUtils'
import { DocumentReviewState } from './types'

const resolveApiErrorMessage = (error: unknown, fallback: string): string => {
  const messages = extractApiErrorMessages(error)

  if (messages.length === 0) {
    return fallback
  }

  return messages.join(', ')
}

const getInitialState = (): DocumentReviewState => ({
  steps: [],
  stepsLoading: false,
  stepsError: null,
  step: null,
  stepLoading: false,
  stepError: null,
  rejectionReasons: [],
  submitting: false,
})

export const documentReviewReducer = (
  state: DocumentReviewState = getInitialState(),
  action: Action<unknown>,
): DocumentReviewState => {
  switch (action.type) {
    case AdminFetchDocumentReviewSteps.type.REQUEST:
      return {
        ...state,
        stepsLoading: true,
        stepsError: null,
      }
    case AdminFetchDocumentReviewSteps.type.SUCCESS:
      return {
        ...state,
        steps: (action.payload as DocumentReviewQueueRow[]) ?? [],
        stepsLoading: false,
        stepsError: null,
      }
    case AdminFetchDocumentReviewSteps.type.FAILED:
      return {
        ...state,
        stepsLoading: false,
        stepsError: resolveApiErrorMessage(action.payload, 'Failed to load the review queue'),
      }

    case AdminFetchDocumentReviewStep.type.REQUEST:
      return {
        ...state,
        // Cleared so the screen cannot show the previous document's details next to the new
        // document's file while the request is in flight.
        step: null,
        stepLoading: true,
        stepError: null,
      }
    case AdminFetchDocumentReviewStep.type.SUCCESS:
      return {
        ...state,
        step: action.payload as DocumentReviewStep,
        stepLoading: false,
        stepError: null,
      }
    case AdminFetchDocumentReviewStep.type.FAILED:
      return {
        ...state,
        stepLoading: false,
        stepError: resolveApiErrorMessage(action.payload, 'Failed to load the document'),
      }

    case AdminFetchReviewRejectionReasons.type.SUCCESS:
      return {
        ...state,
        rejectionReasons: (action.payload as ReviewRejectionReason[]) ?? [],
      }

    case AdminApproveReviewStep.type.REQUEST:
    case AdminRecollectReviewStep.type.REQUEST:
      return { ...state, submitting: true }

    case AdminApproveReviewStep.type.SUCCESS:
    case AdminApproveReviewStep.type.FAILED:
    case AdminRecollectReviewStep.type.SUCCESS:
    case AdminRecollectReviewStep.type.FAILED:
      return { ...state, submitting: false }

    default:
      return state
  }
}
